import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getActById,
  handleCreateAct,
  handleUpdateAct,
} from "../../../API/Mediatheque/Administration/actService";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import PdfFileUpload from "../../FileUpload/PdfFileUpload";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Separator } from "../../ui/separator";
import {
  ActFormValues,
  actSchema,
  UpdatedActFormValues,
  updatedActSchema,
} from "./administration-zod-schema";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../../ui/calendar";
import { LoadingOverlay } from "../../ui/loading-overlay";

export const AdministrationForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const [loading, setLoading] = useState(false);
  const title = initialData ? "Modifier le fichier" : "Ajouter un fichier";
  const description = initialData
    ? "Modifier le fichier"
    : "Ajouter un nouveau fichier";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<ActFormValues | UpdatedActFormValues>({
    resolver: zodResolver(initialData ? updatedActSchema : actSchema),
    defaultValues: {
      title: "",
      date: undefined,
      category: "",
      subCategory: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateAct(initialData, formData, router);
      } else {
        await handleCreateAct(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    {
      title: "Actes administratifs",
      link: "/admin-dashboard/mediatheque/actes-administratifs",
    },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/mediatheque/actes-administratifs/ajouter",
    },
  ];
  const categories = [
    { id: 1, designation: "Comité", value: "comité" },
    { id: 2, designation: "Décisions", value: "décisions" },
    { id: 3, designation: "Bureau", value: "bureau" },
    { id: 4, designation: "Arretes", value: "arretes" },
  ];

  const subCategories = [
    { id: 1, designation: "Ordre du jour", value: "ordre du jour" },
    { id: 2, designation: "PV", value: "pv" },
    { id: 3, designation: "Délibérations", value: "délibérations" },
    {
      id: 4,
      designation: "Liste des Délibérations",
      value: "liste des délibérations",
    },
  ];

  const [data, setData] = useState<any>();

  // Watcher pour réinitialiser subCategory quand category change
  const watchedCategory = form.watch("category");
  useEffect(() => {
    if (watchedCategory && ["décisions", "arretes"].includes(watchedCategory)) {
      form.setValue("subCategory", "");
    }
  }, [watchedCategory]);

  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const actData = await getActById(initialData);

        setData(actData);
        form.reset({
          title: actData.title,
          category: actData.category,
          subCategory: actData.subCategory,
          date: actData.date ? new Date(actData.date) : undefined,
          file: actData.file
            ? {
                fileName: actData.file,
                name: actData.file,
                fileSize: 0,
                size: 0,
                key: actData.file,
              }
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);
  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />

          <LoadingOverlay isVisible={loading} />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between">
                <div className="w-full">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-full ml-3">
                  <div className="flex gap-4">
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem className="flex-1 w-full">
                          <FormLabel>Catégorie</FormLabel>
                          <Select
                            disabled={loading}
                            onValueChange={field.onChange}
                            value={field.value}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Sélectionner catégorie" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {categories?.map((cat) => (
                                <SelectItem key={cat.id} value={cat.value}>
                                  {cat.designation}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {["comité", "bureau"].includes(
                      form.watch("category") ?? ""
                    ) && (
                      <FormField
                        control={form.control}
                        name="subCategory"
                        render={({ field }) => (
                          <FormItem className="flex-1 w-full">
                            <FormLabel>Sous catégorie</FormLabel>
                            <Select
                              disabled={loading}
                              onValueChange={field.onChange}
                              value={field.value}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Sélectionner sous catégorie" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {subCategories?.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.value}>
                                    {cat.designation}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col mt-3">
                      <FormLabel>Date de dépot</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[280px] justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {field.value ? (
                              field.value.toLocaleDateString("fr-FR")
                            ) : (
                              <span>Date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {data?.file && (
                <div className="mt-4 mb-4">
                  <a
                    href={`${uploadUrl}/medias/acts/${data.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    Consulter l'ancien PDF
                  </a>
                </div>
              )}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <PdfFileUpload
                        onChange={(files) => {
                          const file = files[0];
                          if (file) {
                            const mappedFile = {
                              key: file.id,
                              fileName: file.fileName,
                              name: file.fileName,
                              fileSize: parseInt(file.fileSize),
                              size: parseInt(file.fileSize),
                              file: file.file,
                            };
                            form.setValue("file", mappedFile);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
